import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PaymentStatus } from '../../../../components/payment/PaymentStatus';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { NextSection } from '../../../../components/buttons/NextSection';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { NavForward } from '../../../../components/buttons/NavForward';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { updatePaymentInfoDocPayload } from '../../../../types/Payment/updatePaymentInfoDocPayload';
import * as Sentry from '@sentry/browser';

// Make sure to call `loadStripe` outside of a component’s render to avoid recreating the `Stripe` object on every render.
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export const Route = createFileRoute('/_authenticated/$patientId/payment/thanks')({
  component: Thanks,
});

function Thanks() {
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const { patientId } = Route.useParams<{ patientId: string }>();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const succeeded = window.location.href.includes('succeeded');

  const updatePaymentInfoDoc = React.useCallback(
    (payload: updatePaymentInfoDocPayload) => {
      mutateChartDoc(
        {
          patientId: patientId,
          docData: {
            PAYMENT_INFO: {
              alreadyPaid: payload.alreadyPaid,
              amountPaid: payload.amountPaid,
              couponCode: payload.couponCode,
              discountApplied: payload.discountApplied,
            },
          },
        },
        {
          onSuccess: () => {
            // alert('Successfully updated document');
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    },
    [mutateChartDoc, patientId],
  );

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'thanks', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'thanks',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  React.useEffect(() => {
    if (chart && chart.PAYMENT_INFO) {
      if (succeeded) {
        // MARK PAYMENT AS COMPLETED}
        updatePaymentInfoDoc({
          alreadyPaid: true,
          amountPaid: chart.PAYMENT_INFO.amountPaid,
          couponCode: chart.PAYMENT_INFO.couponCode ? chart.PAYMENT_INFO.couponCode : '',
          discountApplied: chart.PAYMENT_INFO.discountApplied ? chart.PAYMENT_INFO.discountApplied : 0,
        });
      }
    }
  }, [chart, updatePaymentInfoDoc, succeeded]);

  return (
    <div id="thanks-page-container" className="flex h-screen flex-col items-center justify-center">
      {/* SHOW STRIPE PAYMENT STATUS */}
      {chart && chart?.PAYMENT_INFO.amountPaid !== 0 && (
        <div className="flex flex-col items-center justify-center text-center text-xl">
          <Elements stripe={stripePromise}>
            <PaymentStatus />
          </Elements>
          {succeeded && <h1 className="text-center">For a copy of your receipt please email info@mideohealth.com</h1>}
        </div>
      )}

      {/* SHOW DISCOUNT CODE APPLIED MESSAGE */}
      {succeeded && chart?.PAYMENT_INFO.discountApplied && chart?.PAYMENT_INFO.discountApplied > 0 && (
        <h1 className="text-xl">Discount Code Applied</h1>
      )}

      {/* SHOW SUCCESSFULLY PRE-PAYING CONCIERGE-GUIDED USERS A SUCCESS MESSAGE AND THE NEXT PAGE BUTTON TO CONTINUE TO INSURANCE OR MEDICARE */}
      {nextPageURL &&
        succeeded &&
        patientInfo?.navType?.base === 'concierge' &&
        (chart?.PAYMENT_INFO.isPrePaidInsurance || chart?.PAYMENT_INFO?.isPrePaidMedicare) && (
          <div className="flex flex-col items-center justify-center text-center">
            <h1 className="text-xl">Payment applied. You may now continue to the next page</h1>
            <NavForward to={nextPageURL} />
          </div>
        )}
      {/* SHOW SUCCESSFULLY PAID CONCIERGE-GUIDED USERS A SUCCESS MESSAGE AND THE NEXT SECTION BUTTON */}
      {nextPageURL &&
        patientInfo?.conciergeNavigator?.photosUnlocked === false &&
        chart?.PAYMENT_INFO.isPrePaidInsurance === false &&
        chart?.PAYMENT_INFO?.isPrePaidMedicare === false && (
          <p className="text-xl">Please wait for your concierge to unlock the next section</p>
        )}
      {nextPageURL && patientInfo?.conciergeNavigator?.photosUnlocked && <NextSection to={nextPageURL} />}

      {/* SHOW SUCCESSFULLY PAID SELF-GUIDED USERS A SUCCESS MESSAGE AND THE NEXT PAGE BUTTON */}
      {patientInfo?.navType.base === 'self' && chart && chart?.PAYMENT_INFO.amountPaid === 0 && (
        <h1 className="text-xl">You may now continue to the next page</h1>
      )}
      {patientInfo?.navType.base === 'self' && nextPageURL && <NavForward to={nextPageURL} />}
    </div>
  );
}
